import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { routes } from '@sketch/constants'

// Containers
import { ModalContext } from 'containers/ModalManager'

// Components
import { Link } from '@sketch/components'
import Section from 'components/Section'

// Local Components
import TokenList from '../../components/TokenList'
import CreateTokenModal from '../../modals/CreateTokenModal'

// Styles
import {
  StyledButton,
  SsoPermissionsCallout,
} from './ManagePersonalTokensView.styles'
import { ReactComponent as InformationIcon } from '@sketch/icons/information-16-svg'

// Operations
import { useSignInToWorkspace, useGetPersonalTokens } from '../../operations'

import {
  useGetInitialUserQuery,
  useGetUserSettingsQuery,
  WorkspaceMinimalFragment,
  UserWorkspaceMembershipFragment,
} from '@sketch/gql-types'

interface ManagePersonalTokensViewProps {
  currentWorkspace: WorkspaceMinimalFragment | undefined
}

const maybePermissionsCallout = (
  workspace: WorkspaceMinimalFragment | undefined,
  memberships: UserWorkspaceMembershipFragment[]
) => {
  if (!workspace) return

  const { ssoEnabled } = workspace.customer || {}
  const isSsoWorkspace = ssoEnabled && workspace.userRole !== 'GUEST'
  if (!isSsoWorkspace) return

  const workspaceMembership = memberships.find(
    membership => membership.workspace.identifier === workspace.identifier
  )
  if (!workspaceMembership) return

  const showSsoPermissionsCallout =
    isSsoWorkspace &&
    workspaceMembership.personalTokenPermissions === 'SSO_MEMBER_DENY'
  if (!showSsoPermissionsCallout) return

  return (
    <SsoPermissionsCallout>
      <InformationIcon width={16} height={16} />
      <div>
        Your tokens do not have access to the “<b>{workspace.name}</b>” SSO
        Workspace.
        <br />
        Contact your Workspace Admin to get access.
      </div>
    </SsoPermissionsCallout>
  )
}

const ManagePersonalTokensView: React.FC<ManagePersonalTokensViewProps> = props => {
  const { currentWorkspace } = props
  const { showModal } = useContext(ModalContext)
  const { personalTokens, loading } = useGetPersonalTokens()
  const location = useLocation()
  const { signIn } = useSignInToWorkspace()
  const { data, loading: loadingUserData } = useGetInitialUserQuery()
  const { data: userSettingsData } = useGetUserSettingsQuery()

  useEffect(() => {
    // Users are required to authenticate before they can create tokens.
    // SSO authentication involves redirecting to an external host (their
    // Identity Provider) and then redirecting back our app on success.
    // We use the `/create` route to differentiate between loading the access
    // tokens page, and SSO login redirects - and open the `CreateTokenModal`
    // automatically on the `/create` route.
    const createRoute = routes.PERSONAL_SETTINGS_ACCESS_TOKENS_CREATE.template()
    if (location.pathname === createRoute) {
      showModal(CreateTokenModal, { redirectToTokensPage: true })
    }
  }, [location.pathname, showModal])

  const userName = data?.me?.name ?? ''
  const userEmail = data?.me?.email ?? ''
  const avatarSrc = data?.me?.avatar?.small ?? ''
  const workspaceMemberships =
    userSettingsData?.me.workspaceMemberships.entries || []

  const handleCreateTokenClick = () => {
    const onSuccess = () => {
      showModal(CreateTokenModal, { redirectToTokensPage: false })
    }

    signIn({
      email: userEmail,
      name: userName,
      avatarSrc: avatarSrc,
      workspace: currentWorkspace,
      onSuccess,
    })
  }

  return (
    <>
      {maybePermissionsCallout(currentWorkspace, workspaceMemberships)}
      <Section
        title="Personal Access Tokens"
        text={
          <div>
            Personal access tokens enable you to access Sketch documents using
            an API.{' '}
            <Link
              // TODO: Update link when docs are available
              // https://github.com/sketch-hq/Cloud/issues/12605
              href="http://www.sketch.com/docs/workspaces/managing-your-sketch-account/#two-factor-authentication-2fa"
              isUnderlined
              external
            >
              Learn more
            </Link>
          </div>
        }
        action={
          <StyledButton
            variant="primary"
            onClick={handleCreateTokenClick}
            size="40"
            disabled={loadingUserData}
          >
            Create Token...
          </StyledButton>
        }
      />
      {!loading && personalTokens && personalTokens.length > 0 && (
        <TokenList tokens={personalTokens} />
      )}
    </>
  )
}

export default ManagePersonalTokensView
